/**
 * OverlayTeamOwner — public team-owner overlay (Iter30 redesign).
 * Dark navy / gold theme matching PublicAuctionDetail. Header shows the
 * live current player. Tabs: Teams / Players / Match with the same card UI
 * and mobile-first responsive layout.
 */
import React, { useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Trophy, CaretLeft, CaretRight, SpeakerHigh, SpeakerSlash, DownloadSimple } from "@phosphor-icons/react";
import { resolveImageUrl } from "@/components/ImageInput";
import {
  PHOTO_FALLBACK, SaleBanner, useSaleAnnouncement, usePublicAuction, useOverlayState,
  FIELD_LABELS, readPlayerField, getLedDisplayFields, useAccentStyle, brandLogoFor,
} from "./_shared";

function cx(...arr) { return arr.filter(Boolean).join(" "); }

export function OverlayTeamOwner() {
  const { auctionId } = useParams();
  const navigate = useNavigate();
  const { auction, teams, players, reload } = usePublicAuction(auctionId);
  const state = useOverlayState(auctionId, reload);

  const cur = state?.current_player;
  const bidder = state?.current_bidder_team;
  const currentBid = state?.current_bid || 0;
  const ledExtraFields = getLedDisplayFields(auction);
  const accentStyle = useAccentStyle(auction);
  const settings = auction?.settings || {};
  const basePrice = settings.default_base_price || 10;
  const minPlayers = settings.min_players_per_team || 11;

  const enrichedTeams = useMemo(() => teams.map((t) => {
    const tp = players.filter((p) => p.sold_to === t.id);
    const bought = tp.length;
    const slotsAfter = Math.max(0, minPlayers - bought - 1);
    const maxBid = Math.max(0, (t.remaining_budget || 0) - slotsAfter * basePrice);
    return { ...t, players_bought: bought, max_bid: maxBid, players: tp, spent: tp.reduce((s, p) => s + (p.sold_price || 0), 0) };
  }), [teams, players, basePrice, minPlayers]);

  const soldPlayers = players.filter((p) => p.status === "sold");
  const availPlayers = players.filter((p) => p.status === "available");
  const unsoldPlayers = players.filter((p) => p.status === "unsold");
  const categories = Array.from(new Set(players.map((p) => p.category).filter(Boolean)));

  const [tab, setTab] = useState("teams");
  const [muted, setMuted] = useState(true);
  const [sortKey, setSortKey] = useState("");
  const [category, setCategory] = useState("ALL");
  const [statusFilter, setStatusFilter] = useState("All");
  const [teamIdx, setTeamIdx] = useState(0);

  const announce = useSaleAnnouncement(auctionId, players, teams);

  return (
    <div className="min-h-screen text-white" style={{ background: "linear-gradient(180deg,#071731 0%,#0A2350 60%,#082042 100%)", ...accentStyle }} data-testid="overlay-team-owner">
      <SaleBanner announce={announce} auction={auction} position="center" />

      {/* Header with live player */}
      <div className="border-b border-white/5 bg-[#061428]/80 backdrop-blur">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 py-3 flex items-center gap-3 flex-wrap">
          <img src={brandLogoFor(auction)} alt="" className="h-8" />
          <div className="flex-1 min-w-0">
            <div className="text-[10px] font-black uppercase tracking-[0.2em] text-yellow-300/90 truncate">{auction?.name || "AUCTION"} · TEAM-OWNER VIEW</div>
            <div className="text-[11px] text-white/60 truncate">Public overlay · shareable</div>
          </div>
          {cur ? (
            <div className="flex items-center gap-3 bg-[#0F2C5A] border border-yellow-400/30 rounded px-3 py-2 w-full sm:w-auto">
              <div className="w-10 h-10 rounded bg-black/40 overflow-hidden flex-shrink-0">
                <img src={cur.photo_url ? resolveImageUrl(cur.photo_url) : PHOTO_FALLBACK} alt="" onError={(e) => { e.target.src = PHOTO_FALLBACK; }} className="w-full h-full object-cover" />
              </div>
              <div className="min-w-0">
                <div className="text-[10px] font-black uppercase tracking-widest text-yellow-300 flex items-center gap-1"><span className="live-dot" /> LIVE · {cur.role}</div>
                <div className="font-black text-white text-base leading-none truncate">{cur.name}</div>
                {ledExtraFields.length > 0 && (
                  <div className="mt-1 flex flex-wrap gap-x-2 gap-y-0.5 text-[10px] text-white/60" data-testid="to-extra-stats">
                    {ledExtraFields.map((k) => {
                      const v = readPlayerField(cur, k);
                      if (v == null || v === "") return null;
                      return <span key={k} data-testid={`to-extra-${k}`}><span className="text-yellow-400 font-bold mr-0.5">{FIELD_LABELS[k] || k.toUpperCase()}</span>{v}</span>;
                    })}
                  </div>
                )}
              </div>
              <div className="text-right ml-auto">
                <div className="font-black text-2xl text-emerald-300 leading-none tabular-nums">{currentBid}</div>
                <div className="text-[10px] font-black uppercase tracking-widest text-white/60">{bidder?.short_name || "no bid"}</div>
              </div>
            </div>
          ) : (
            <div className="text-yellow-300/70 text-xs font-black uppercase tracking-widest">No active player</div>
          )}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-3 sm:px-6">
        {/* Toolbar */}
        <div className="sticky top-0 z-30 py-3 -mx-3 sm:-mx-6 px-3 sm:px-6 backdrop-blur bg-[#071731]/85 border-b border-white/5">
          <div className="flex items-center gap-2 sm:gap-3 flex-nowrap overflow-x-auto">
            <SegBtn active={tab === "teams"} onClick={() => setTab("teams")} testId="to-tab-teams">Teams</SegBtn>
            <SegBtn active={tab === "players"} onClick={() => setTab("players")} testId="to-tab-players">Players</SegBtn>
            <div className="flex items-center gap-2 ml-auto">
              <Pill color="emerald" label="SOLD" value={soldPlayers.length} />
              <Pill color="rose" label="UNSOLD" value={unsoldPlayers.length} />
              <Pill color="amber" label="AVL" value={availPlayers.length} />
              <button type="button" onClick={() => setMuted((m) => !m)} className="text-yellow-300 hover:text-yellow-200 p-1.5" aria-label="Toggle sound">
                {muted ? <SpeakerSlash weight="fill" size={20} /> : <SpeakerHigh weight="fill" size={20} />}
              </button>
              <select value={sortKey} onChange={(e) => setSortKey(e.target.value)} className="bg-[#0F2C5A] border border-white/10 rounded px-2 py-1.5 text-xs font-bold text-yellow-300 focus:border-yellow-400 outline-none" data-testid="to-sort">
                <option value="">Sort By ▼</option>
                <option value="name">Name</option>
                <option value="points-desc">Points ↓</option>
                <option value="points-asc">Points ↑</option>
                <option value="number">Number</option>
              </select>
            </div>
          </div>
        </div>

        {tab === "teams" && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 py-4" data-testid="to-teams-grid">
            {enrichedTeams.length === 0 && <div className="col-span-full text-center text-white/60 py-10">No teams yet.</div>}
            {enrichedTeams.map((t) => (
              <button key={t.id} onClick={() => navigate(`/auction/${auctionId}/team/${t.id}`)} type="button" className="text-left rounded-xl bg-[#0F2C5A] hover:bg-[#12356B] border border-white/5 p-3 sm:p-4 flex items-center gap-3 sm:gap-4" data-testid={`to-team-${t.id}`}>
                {t.logo_url ? (
                  <img src={resolveImageUrl(t.logo_url)} alt="" className="w-14 h-14 sm:w-16 sm:h-16 rounded object-cover bg-white flex-shrink-0" onError={(e) => { e.target.style.display = "none"; }} />
                ) : (
                  <div className="w-14 h-14 sm:w-16 sm:h-16 rounded grid place-items-center flex-shrink-0" style={{ background: `${t.color}22`, border: `1px solid ${t.color}55` }}>
                    <Trophy weight="fill" size={22} style={{ color: t.color }} />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="text-white font-bold text-sm sm:text-base truncate">{t.name}</div>
                  <div className="flex items-baseline gap-3 mt-1">
                    <div className="text-yellow-300 font-black text-2xl sm:text-3xl tabular-nums leading-none">{t.players_bought}</div>
                    <div className="ml-auto text-right">
                      <div className="text-yellow-300 font-black text-xl sm:text-2xl tabular-nums leading-none">{(t.budget || 0).toLocaleString()}</div>
                    </div>
                  </div>
                  <div className="flex items-center justify-between mt-1 text-[11px] sm:text-xs">
                    <div className="text-white/70">Max <span className="text-white font-bold">{t.max_players || 15}</span></div>
                    <div className="text-white/70">Max Bid <span className="text-amber-300 font-black tabular-nums">{(t.max_bid || 0).toLocaleString()}</span></div>
                    <div className="text-white/70">Res <span className="text-emerald-400 font-black tabular-nums">{(t.remaining_budget ?? 0).toLocaleString()}</span></div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}

        {tab === "players" && (
          <PlayersSection
            auctionId={auctionId}
            players={applySort(applyCategory(applyStatus(players, statusFilter), category), sortKey)}
            teams={enrichedTeams}
            categories={categories}
            category={category}
            onCategory={setCategory}
            statusFilter={statusFilter}
            onStatusFilter={setStatusFilter}
            totalCount={players.length}
          />
        )}
      </div>
    </div>
  );
}

// ————————————————— helpers shared with PublicAuctionDetail —————————————————
function applyStatus(list, s) {
  if (s === "AVL") return list.filter((p) => p.status === "available");
  if (s === "SOLD") return list.filter((p) => p.status === "sold");
  if (s === "UNSOLD") return list.filter((p) => p.status === "unsold");
  return list;
}
function applyCategory(list, c) {
  if (!c || c === "ALL") return list;
  if (c === "No category") return list.filter((p) => !p.category);
  return list.filter((p) => p.category === c);
}
function applySort(list, key) {
  const arr = [...list];
  if (key === "name") arr.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  else if (key === "points-desc") arr.sort((a, b) => (b.sold_price || b.base_price || 0) - (a.sold_price || a.base_price || 0));
  else if (key === "points-asc") arr.sort((a, b) => (a.sold_price || a.base_price || 0) - (b.sold_price || b.base_price || 0));
  else if (key === "number") arr.sort((a, b) => (a.player_number || 0) - (b.player_number || 0));
  return arr;
}

function SegBtn({ active, onClick, children, testId }) {
  return (
    <button type="button" onClick={onClick} data-testid={testId}
      className={cx(
        "text-xs sm:text-sm font-black uppercase tracking-widest px-4 sm:px-5 py-1.5 rounded-full whitespace-nowrap transition-all",
        active ? "bg-gradient-to-r from-amber-400 to-yellow-500 text-slate-900 shadow-lg shadow-yellow-500/20" : "text-white/80 hover:text-yellow-300",
      )}>{children}</button>
  );
}
function Pill({ color, label, value }) {
  const map = { emerald: "bg-emerald-500/90 text-white border-emerald-300/40", rose: "bg-rose-500/90 text-white border-rose-300/40", amber: "bg-amber-500/90 text-slate-900 border-amber-300/40" };
  return (
    <span className={cx("inline-flex items-center gap-1.5 rounded-full border text-[10px] sm:text-xs font-black uppercase tracking-widest px-2.5 sm:px-3 py-1", map[color])}>
      {label} : <span className="tabular-nums text-white">{value}</span>
    </span>
  );
}
function CategoryChips({ categories, category, onCategory, totalCount }) {
  const chips = ["ALL", ...categories]; if (categories.length) chips.push("No category");
  return (
    <div className="flex items-center gap-2 sm:gap-3 overflow-x-auto py-2 sticky top-[52px] sm:top-[56px] bg-[#071731]/95 z-20 -mx-3 sm:-mx-6 px-3 sm:px-6">
      {totalCount != null && <span className="text-yellow-300 font-black text-lg sm:text-xl tabular-nums pr-2">{totalCount}</span>}
      {chips.map((c) => (
        <button key={c} onClick={() => onCategory(c)} type="button"
          className={cx("text-[11px] sm:text-xs font-black uppercase tracking-widest px-3 sm:px-4 py-1.5 rounded-md whitespace-nowrap transition-colors border",
            category === c ? "bg-gradient-to-r from-amber-400 to-yellow-500 text-slate-900 border-yellow-300" : "bg-transparent text-white/80 border-white/10 hover:border-yellow-400/50 hover:text-yellow-300")}>{c}</button>
      ))}
    </div>
  );
}
function StatusFilterRow({ statusFilter, onStatusFilter }) {
  const items = ["All", "AVL", "SOLD", "UNSOLD"];
  return (
    <div className="flex items-center gap-2 flex-wrap py-1">
      {items.map((k) => (
        <button key={k} onClick={() => onStatusFilter(k)}
          className={cx("text-[11px] font-black uppercase tracking-widest px-3 py-1 rounded-md border transition-colors",
            statusFilter === k ? "bg-white/10 text-yellow-300 border-yellow-400/60" : "text-white/70 border-white/10 hover:border-yellow-400/40")}>{k}</button>
      ))}
    </div>
  );
}
function PlayerCard({ p, team, auctionId }) {
  const pts = p.sold_price != null ? p.sold_price : p.base_price;
  const isIcon = p.is_icon || p.category === "Icon";
  return (
    <div className={cx("rounded-xl border overflow-hidden relative", isIcon ? "bg-[#1a2a4a] border-amber-400/70 ring-1 ring-amber-400/30" : "bg-[#0F2C5A] border-white/5")}>
      {p.player_number != null && (
        <span className="absolute top-2 left-2 z-10 bg-gradient-to-br from-amber-400 to-yellow-500 text-slate-900 font-black text-[10px] sm:text-xs px-2 py-0.5 rounded-md shadow">{p.player_number}</span>
      )}
      {isIcon && (
        <span className="absolute top-2 left-1/2 -translate-x-1/2 z-10 bg-gradient-to-br from-amber-400 to-yellow-500 text-slate-900 font-black text-[9px] sm:text-[10px] px-2 py-0.5 rounded-md tracking-widest shadow">★ ICON</span>
      )}
      {auctionId && (
        <a href={`/auction/${auctionId}/player/${p.id}/card`} target="_blank" rel="noreferrer" className="absolute top-2 right-2 z-10 rounded-md bg-black/70 hover:bg-yellow-400 hover:text-slate-900 text-yellow-300 p-1.5 transition-colors" title="Open downloadable player card" aria-label="Download player card">
          <DownloadSimple weight="bold" size={14} />
        </a>
      )}
      <div className="flex items-stretch gap-3 p-3 pt-6">
        <div className="relative w-20 h-24 sm:w-24 sm:h-28 rounded-md overflow-hidden bg-black/30 flex-shrink-0">
          {p.photo_url ? <img src={resolveImageUrl(p.photo_url)} alt="" className="w-full h-full object-cover" onError={(e) => { e.target.style.display = "none"; }} /> : <div className="w-full h-full grid place-items-center text-white/40 text-[10px]">NO PHOTO</div>}
          {isIcon ? (
            <span className="absolute bottom-0 inset-x-0 text-center text-[9px] sm:text-[10px] font-black uppercase tracking-widest text-slate-900 bg-gradient-to-r from-amber-400 to-yellow-500 py-0.5 truncate px-1" title={p.name}>{p.name}</span>
          ) : p.category && <span className="absolute bottom-0 inset-x-0 text-center text-[9px] sm:text-[10px] font-black uppercase tracking-widest text-emerald-300 bg-black/50 py-0.5">{p.category}</span>}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-yellow-300 font-black text-base sm:text-lg leading-tight truncate">{p.name}</div>
          {pts != null && <div className="text-white text-xs sm:text-sm mt-1"><span className="text-white/60">Points : </span><span className="font-black tabular-nums text-white">{pts.toLocaleString()}</span></div>}
          {p.role && <div className="text-white/80 text-xs sm:text-sm truncate">{p.role}</div>}
          {p.batting_style && <div className="text-white/70 text-xs sm:text-sm truncate">{p.batting_style}</div>}
          {p.bowling_style && <div className="text-white/70 text-xs sm:text-sm truncate">{p.bowling_style}</div>}
          {team && <div className="text-emerald-300 text-[11px] mt-1">→ {team.short_name || team.name}</div>}
        </div>
      </div>
    </div>
  );
}
function PlayersSection({ auctionId, players, teams, categories, category, onCategory, statusFilter, onStatusFilter, totalCount }) {
  const teamMap = Object.fromEntries(teams.map((t) => [t.id, t]));
  return (
    <div className="pb-8">
      <div className="flex items-center gap-3 flex-wrap py-2"><StatusFilterRow statusFilter={statusFilter} onStatusFilter={onStatusFilter} /></div>
      <CategoryChips categories={categories} category={category} onCategory={onCategory} totalCount={totalCount} />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 pt-2">
        {players.length === 0 && <div className="col-span-full text-center text-white/60 py-10">No players in this view.</div>}
        {players.map((p) => <PlayerCard key={p.id} p={p} team={p.sold_to ? teamMap[p.sold_to] : null} auctionId={auctionId} />)}
      </div>
    </div>
  );
}
function TeamPlayerCarousel({ auctionId, teams, teamIdx, setTeamIdx, categories, category, onCategory, sortKey }) {
  const t = teams[teamIdx];
  if (!t) return <div className="text-center text-white/60 py-16">No teams yet.</div>;
  const iconFirst = (arr) => [...arr].sort((a, b) => {
    const ai = (a.is_icon || a.category === "Icon") ? 0 : 1;
    const bi = (b.is_icon || b.category === "Icon") ? 0 : 1;
    return ai - bi;
  });
  const list = iconFirst(applyCategory(applySort(t.players || [], sortKey), category));
  const catCounts = categories.reduce((acc, c) => { acc[c] = (t.players || []).filter((p) => p.category === c).length; return acc; }, {});
  return (
    <div className="pb-8">
      <div className="flex items-center gap-3 py-3">
        <button onClick={() => setTeamIdx((i) => (i - 1 + teams.length) % teams.length)} className="p-2 rounded-full bg-white/5 hover:bg-yellow-400/20 text-yellow-300"><CaretLeft weight="bold" /></button>
        <div className="flex-1 min-w-0">
          <div className="text-white font-black text-xl sm:text-2xl truncate">{t.name}</div>
          <div className="text-yellow-300 text-lg sm:text-xl font-black tabular-nums">{t.players_bought}</div>
        </div>
        <button onClick={() => setTeamIdx((i) => (i + 1) % teams.length)} className="p-2 rounded-full bg-white/5 hover:bg-yellow-400/20 text-yellow-300"><CaretRight weight="bold" /></button>
      </div>
      <CategoryChips categories={categories} category={category} onCategory={onCategory} />
      <div className="flex flex-wrap gap-3 text-[11px] text-white/70 mt-2">
        {Object.entries(catCounts).map(([c, n]) => <span key={c}><span className="text-white font-bold">{c}: {n}</span></span>)}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 pt-3">
        {list.length === 0 && <div className="col-span-full text-center text-white/60 py-10">No players yet.</div>}
        {list.map((p) => <PlayerCard key={p.id} p={p} team={t} auctionId={auctionId} />)}
      </div>
    </div>
  );
}
